// frontend/src/site/editor/ThemePicker.jsx
import React, { useMemo } from 'react';

// ✅ fonte oficial de modelos (agora: 2 modelos)
import { SITE_MODELS } from '../builder/siteSections';
import { SITE_PRESETS } from '../theme/presets';

// ✅ preview real por tema
import { getSiteTheme, themeToVars } from '../theme/getSiteTheme';

// ------------------------------
// Estilos (Quadrante 01) — agora só 3
// ------------------------------
const STYLES = [
  {
    id: 'humano',
    label: 'Humano',
    desc: 'Próximo, consultivo e acessível',
    badges: ['Consultivo', 'Relacionamento', 'Confiança'],
  },
  {
    id: 'autoridade',
    label: 'Autoridade',
    desc: 'Institucional, sério, confiável',
    badges: ['Credibilidade', 'Institucional', 'Profissional'],
  },
  {
    id: 'premium',
    label: 'Premium',
    desc: 'Alto padrão, sofisticado',
    badges: ['Alto padrão', 'Exclusividade', 'Sofisticação'],
  },
];

const PALETTES = [
  { id: 'a', label: 'Paleta A' },
  { id: 'b', label: 'Paleta B' },
  { id: 'c', label: 'Paleta C' },
];

// UI (mantido por compatibilidade), mas tema está travado no preset via getSiteTheme
const DENSITIES = [
  { id: 'compact', label: 'Compacta' },
  { id: 'normal', label: 'Normal' },
  { id: 'airy', label: 'Arejada' },
];

const BUTTON_STYLES = [
  { id: 'solid', label: 'Solid' },
  { id: 'outline', label: 'Outline' },
  { id: 'ghost', label: 'Ghost' },
];

const CARD_STYLES = [
  { id: 'soft', label: 'Soft' },
  { id: 'hard', label: 'Hard' },
  { id: 'glass', label: 'Glass' },
];

// ------------------------------
// Helpers
// ------------------------------
function stylePaletteToPresetId(styleId, paletteId) {
  const s = String(styleId || 'humano')
    .trim()
    .toLowerCase();
  const p = String(paletteId || 'a')
    .trim()
    .toLowerCase();
  return `${s}_${p}`;
}

function inferStyleFromPreset(preset) {
  const x = String(preset || '')
    .trim()
    .toLowerCase();
  if (x.startsWith('premium_')) return 'premium';
  if (x.startsWith('autoridade_')) return 'autoridade';
  if (x.startsWith('humano_')) return 'humano';
  return 'humano';
}

function inferPaletteFromPreset(preset) {
  const x = String(preset || '')
    .trim()
    .toLowerCase();
  const last = x.split('_').pop();
  return ['a', 'b', 'c'].includes(last) ? last : 'a';
}

// ------------------------------
// Card reutilizável (com badges)
// ------------------------------
function Card({ active, title, desc, badges = [], onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={[
        'text-left border rounded-xl p-4 transition w-full',
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50',
        active ? 'border-black bg-gray-50' : 'border-gray-200 bg-white',
      ].join(' ')}
    >
      <div className="font-medium text-gray-900">{title}</div>

      {desc && <div className="text-xs text-gray-500 mt-1">{desc}</div>}

      {badges.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {badges.map((b) => (
            <span
              key={b}
              className="text-[11px] px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200"
            >
              {b}
            </span>
          ))}
        </div>
      )}

      <div className="mt-3 text-[11px] text-gray-500">
        {active ? 'Selecionado' : 'Clique para aplicar'}
      </div>
    </button>
  );
}

// ------------------------------
// Preview do Card (reage a vars)
// ------------------------------
function CardPreview({ vars, mode = 'light' }) {
  return (
    <div data-theme={mode} style={vars} className="w-full">
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="h-24 w-full rounded-xl bg-gray-100" />

        <div className="mt-4 flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-gray-900">
              Imóvel em Destaque
            </div>
            <div className="text-sm text-gray-600">Sinop - MT • 3 quartos</div>
          </div>
          <div className="font-semibold text-gray-900">R$ 890.000</div>
        </div>

        <button
          type="button"
          className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm bg-black text-white"
        >
          Ver detalhes
        </button>
      </div>
    </div>
  );
}

// ------------------------------
// ThemePicker
// ------------------------------
export default function ThemePicker({
  modelId,
  styleId,
  paletteId,
  preset,
  layoutDensity = 'normal',
  buttonStyle = 'solid',
  cardStyle = 'soft',
  onChange,
  showPreset = true,
  lockModel = false,
}) {
  const presetId = useMemo(() => {
    const p = String(preset || '').trim();
    if (p) return p;
    return stylePaletteToPresetId(styleId, paletteId);
  }, [preset, styleId, paletteId]);

  const effectiveStyleId = useMemo(() => {
    const s = String(styleId || '').trim();
    return s ? s : inferStyleFromPreset(presetId);
  }, [styleId, presetId]);

  const effectivePaletteId = useMemo(() => {
    const p = String(paletteId || '').trim();
    return p ? p : inferPaletteFromPreset(presetId);
  }, [paletteId, presetId]);

  const presetExists = Boolean(SITE_PRESETS?.[presetId]);

  const modelsList = useMemo(() => {
    const vals = SITE_MODELS ? Object.values(SITE_MODELS) : [];
    if (vals.length) return vals;
    return [
      { id: 'corretor', label: 'Corretor (completo)' },
      { id: 'imobiliaria', label: 'Imobiliária (padrão)' },
    ];
  }, []);

  function emit(next) {
    if (
      lockModel &&
      next?.modelId &&
      String(next.modelId) !== String(modelId)
    ) {
      const { modelId: _ignored, ...rest } = next;
      onChange?.({ modelId, ...rest });
      return;
    }
    onChange?.(next);
  }

  // ✅ preview REAL usando apenas preset/style/palette (sem fingir que densidade/botão/card mudam)
  const previewTheme = useMemo(() => {
    const fakePerfil = {
      siteConfig: {
        modelId,
        styleId: effectiveStyleId,
        paletteId: effectivePaletteId,
        preset: presetId,
      },
    };
    return getSiteTheme(fakePerfil);
  }, [modelId, effectiveStyleId, effectivePaletteId, presetId]);

  const vars = useMemo(() => themeToVars(previewTheme || {}), [previewTheme]);

  return (
    <div className="space-y-4">
      {/* MODELO */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-gray-900">Modelo</div>
            <div className="text-sm text-gray-500">
              Define quais seções existem e o comportamento do site.
            </div>
          </div>

          {lockModel && (
            <div className="text-xs px-2 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700">
              Modelo travado
            </div>
          )}
        </div>

        {lockModel ? (
          <div className="mt-3 text-sm text-gray-600">
            Selecionado:{' '}
            <span className="font-medium text-gray-900">{modelId}</span>
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            {modelsList.map((m) => (
              <Card
                key={m.id}
                active={modelId === m.id}
                title={m.label || m.id}
                desc={
                  m.id === 'corretor'
                    ? 'Mais completo (profissional + conteúdo)'
                    : 'Institucional, foco em catálogo de imóveis'
                }
                onClick={() =>
                  emit({
                    modelId: m.id,
                    styleId: effectiveStyleId,
                    paletteId: effectivePaletteId,
                    preset: presetId,
                  })
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* ESTILO */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="font-semibold text-gray-900">Estilo do site</div>
        <div className="text-sm text-gray-500">
          Define o posicionamento visual e a percepção profissional.
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          {STYLES.map((s) => (
            <Card
              key={s.id}
              active={effectiveStyleId === s.id}
              title={s.label}
              desc={s.desc}
              badges={s.badges}
              onClick={() => {
                const nextPreset = stylePaletteToPresetId(
                  s.id,
                  effectivePaletteId
                );
                emit({
                  modelId,
                  styleId: s.id,
                  paletteId: effectivePaletteId,
                  preset: nextPreset,
                });
              }}
            />
          ))}
        </div>
      </div>

      {/* PALETA */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="font-semibold text-gray-900">Paleta</div>
        <div className="text-sm text-gray-500">
          Varia as cores dentro do mesmo estilo.
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          {PALETTES.map((p) => (
            <Card
              key={p.id}
              active={effectivePaletteId === p.id}
              title={p.label}
              onClick={() => {
                const nextPreset = stylePaletteToPresetId(
                  effectiveStyleId,
                  p.id
                );
                emit({
                  modelId,
                  styleId: effectiveStyleId,
                  paletteId: p.id,
                  preset: nextPreset,
                });
              }}
            />
          ))}
        </div>

        {showPreset && (
          <div className="mt-3 text-xs text-gray-500">
            Preset aplicado:{' '}
            <b className={presetExists ? 'text-gray-900' : 'text-red-700'}>
              {presetId}
            </b>
          </div>
        )}
      </div>

      {/* AJUSTES FINOS (UI mantida, mas tema travado no preset) */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-gray-900">Ajustes finos</div>
            <div className="text-sm text-gray-500">
              No momento, esses ajustes estão travados pelo estilo/preset.
            </div>
          </div>

          <div className="text-xs px-2 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-800">
            Travado pelo preset
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 opacity-60">
          <div>
            <div className="text-sm font-medium text-gray-900 mb-1">
              Densidade
            </div>
            <select
              value={layoutDensity}
              onChange={(e) => emit({ layoutDensity: e.target.value })}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
              disabled
            >
              {DENSITIES.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-900 mb-1">
              Estilo do botão
            </div>
            <select
              value={buttonStyle}
              onChange={(e) => emit({ buttonStyle: e.target.value })}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
              disabled
            >
              {BUTTON_STYLES.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-900 mb-1">
              Estilo do card
            </div>
            <select
              value={cardStyle}
              onChange={(e) => emit({ cardStyle: e.target.value })}
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
              disabled
            >
              {CARD_STYLES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-sm font-medium text-gray-900 mb-2">
            Preview do card
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <CardPreview vars={vars} mode={previewTheme?.mode || 'light'} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Helpers exportáveis
export function presetToStylePalette(preset) {
  return {
    styleId: inferStyleFromPreset(preset),
    paletteId: inferPaletteFromPreset(preset),
  };
}
